import classNames from 'classnames/bind';
import Button from 'mcs-toolkit/components/Button';
import Modal from 'mcs-toolkit/components/Modal';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { ScrollbarUtils } from 'utilities/ScrollbarUtils';

import { Forms } from 'reactComponents/Forms';
import { ProgressBar } from 'reactComponents/ProgressBar/ProgressBar';

import Loader from '../loader/Loader';
import styles from './Dialog.css';

const css = classNames.bind(styles);

export class Dialog extends PureComponent {
    static propTypes = {
        open: PropTypes.bool.isRequired,
        className: PropTypes.string,
        accept: PropTypes.string,
        cancel: PropTypes.string,
        onAccept: PropTypes.func,
        onCancel: PropTypes.func,
        footer: PropTypes.node,
        size: PropTypes.string,
        loading: PropTypes.bool, // show loader and block closing
        isRequesting: PropTypes.bool, // show loading on button and prevent closing
        isAcceptDisabled: PropTypes.bool,
        waitActionBeforeClose: PropTypes.bool,
        formikParams: PropTypes.object,
        noCommonErrorMessages: PropTypes.bool,
    };

    static defaultProps = {
        waitActionBeforeClose: true,
    };

    state = {};

    componentDidUpdate(prevProps) {
        if (this.props.open !== prevProps.open && this.props.open === true) {
            ScrollbarUtils.hideScroll();
        }
        if (this.props.open !== prevProps.open && prevProps.open === false) {
            ScrollbarUtils.showScroll();
        }
    }

    handleAccept = (...args) => {
        const { formikParams } = this.props;

        if (formikParams && formikParams.handleSubmit) {
            formikParams.handleSubmit(...args);
        } else if (!this.props.isRequesting) {
            this.props.onAccept(...args);
        }
    };

    handleCancel = () => {
        ScrollbarUtils.showScroll();
        const {
            isRequesting,
            progressBarOptions,
            waitActionBeforeClose,
            onCancel,
        } = this.props;

        if (!isRequesting || progressBarOptions || !waitActionBeforeClose) {
            onCancel();
        }
    };

    _renderChildren = () =>
        this.props.children && (
            <Modal.Content>
                {this.props.formikParams ? (
                    <Forms.FormikContext.Provider
                        value={this.props.formikParams}
                    >
                        {this.props.children}

                        {!this.props.noCommonErrorMessages && (
                            <Forms.FieldCommonErrorMessages
                                className={css('fieldCommonErrorMessages')}
                            />
                        )}
                    </Forms.FormikContext.Provider>
                ) : (
                    this.props.children
                )}
            </Modal.Content>
        );

    render() {
        const {
            props: {
                progressBarOptions,
                open,
                className,
                loading,
                header,
                accept,
                cancel,
                footer,
                children,
                size,
                isRequesting,
                isAcceptDisabled,
                mountNode,
                formikParams,
                onAccept,
                onCancel,
                waitActionBeforeClose,
                noCommonErrorMessages,
                actionsClassName,
                ...restDialogParams
            },
            handleAccept,
            handleCancel,
        } = this;

        const canRenderDialog =
            !!(formikParams && this.state.formNode) || !formikParams;

        return (
            <InForm
                formikParams={formikParams}
                innerRef={(formNode) => {
                    this.setState({ formNode });
                }}
            >
                {canRenderDialog && (
                    <Modal
                        {...restDialogParams}
                        closeIcon={!loading}
                        closeOnDimmerClick={!loading && !isRequesting}
                        className={css('main', className)}
                        open={open}
                        size={size}
                        onClose={handleCancel}
                        mountNode={this.state.formNode || mountNode}
                    >
                        {header && <Modal.Header>{header}</Modal.Header>}

                        {loading && <Loader opaque />}
                        {progressBarOptions && isRequesting && (
                            <ProgressBar percent={progressBarOptions.percent} />
                        )}

                        {this._renderChildren()}

                        {(accept || cancel || footer) && (
                            <Modal.Actions className={actionsClassName}>
                                {accept && (
                                    <Button
                                        primary
                                        type="submit"
                                        onClick={handleAccept}
                                        disabled={
                                            isRequesting || isAcceptDisabled
                                        }
                                        loading={isRequesting}
                                        data-test="confirm-button"
                                    >
                                        {accept}
                                    </Button>
                                )}
                                {cancel && (
                                    <Button
                                        type="button"
                                        onClick={handleCancel}
                                        disabled={
                                            isRequesting && !progressBarOptions
                                        }
                                        data-test="cancel-button"
                                    >
                                        {cancel}
                                    </Button>
                                )}
                                {footer}
                            </Modal.Actions>
                        )}
                    </Modal>
                )}
            </InForm>
        );
    }
}

class InForm extends React.PureComponent {
    render() {
        const { children, formikParams, innerRef } = this.props;
        if (formikParams) {
            return (
                <Forms.Form
                    noCommonErrorMessages
                    onSubmit={formikParams.handleSubmit}
                    error={formikParams.hasErrors}
                >
                    <div ref={innerRef}>{children}</div>
                </Forms.Form>
            );
        }
        return children;
    }
}

export default Dialog;
